/**
 * CancelSubscriptionButton — parent-facing self-cancel control.
 *
 * Honors §12 of the waiver:
 *   - Cannot cancel during the initial committed term (button is disabled
 *     with the lock date in the tooltip).
 *   - After the committed term ends, cancel takes effect immediately
 *     (no future charges) OR after the upcoming charge if within 48h.
 *     The backend computes which case applies and surfaces it in the
 *     success toast.
 *
 * Sachin spec 2026-02-28.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    AlertTriangle, Loader2, X, Ban, Lock, ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso; }
}

export default function CancelSubscriptionButton({ commitment, onCancelled }) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [reason, setReason] = useState("");

    const endsAt = commitment?.commitment_ends_at;
    const endsDt = endsAt ? new Date(endsAt) : null;
    const stillInTerm = endsDt && endsDt > new Date();
    const alreadyDone = commitment?.status === "cancelled" || commitment?.status === "completed";

    if (alreadyDone) return null;

    if (stillInTerm) {
        return (
            <button
                type="button"
                data-testid={`self-cancel-locked-${commitment.commitment_id}`}
                disabled
                title={`Cancellation unlocks ${fmt(endsAt)}. Per §12.b of your Participant Agreement, the initial committed term can't be cancelled.`}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-zinc-800 bg-zinc-900/50 text-zinc-500 px-2.5 py-1.5 cursor-not-allowed"
            >
                <Lock className="w-3.5 h-3.5" />
                Locked
            </button>
        );
    }

    const submit = async () => {
        setSubmitting(true);
        try {
            const r = await axios.post(
                `${API}/me/commitments/cancel`,
                { commitment_id: commitment.commitment_id, reason: reason.trim() },
                { withCredentials: true },
            );
            toast.success("Subscription cancelled", {
                description: `Takes effect ${r.data?.effective || "shortly"}.`,
            });
            setOpen(false);
            onCancelled?.();
        } catch (e) {
            toast.error("Couldn't cancel subscription", {
                description: e?.response?.data?.detail,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <button
                type="button"
                data-testid={`self-cancel-btn-${commitment.commitment_id}`}
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-red-700/60 bg-red-900/10 text-red-300 hover:bg-red-700 hover:text-white transition-colors px-2.5 py-1.5"
            >
                <Ban className="w-3.5 h-3.5" />
                Cancel
            </button>

            {open && (
                <div
                    data-testid="self-cancel-modal"
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                    onClick={(e) => { if (e.target === e.currentTarget && !submitting) setOpen(false); }}
                >
                    <div className="bg-zinc-950 border border-zinc-800 w-full max-w-md max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
                            <div>
                                <div className="text-[10px] tracking-[0.25em] uppercase text-red-400">
                                    Cancel subscription
                                </div>
                                <div className="text-sm text-white mt-0.5 truncate">
                                    {commitment.display_name || "Membership"}
                                </div>
                            </div>
                            <button
                                type="button"
                                disabled={submitting}
                                onClick={() => setOpen(false)}
                                className="text-zinc-400 hover:text-white transition-colors p-1 disabled:opacity-30"
                                aria-label="Close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="px-5 py-4 space-y-4">
                            <div className="border border-amber-700/40 bg-amber-900/10 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-100 leading-relaxed">
                                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                                <div>
                                    <strong>48-hour rule.</strong> If your next charge is more
                                    than 48 hours away, this cancels immediately and you won&apos;t
                                    be charged again. If your next charge is within 48 hours, the
                                    upcoming charge is non-refundable and your membership ends
                                    after that period.
                                </div>
                            </div>

                            <div>
                                <label className="block text-[11px] tracking-[0.18em] uppercase text-zinc-400 mb-1.5">
                                    Optional — tell us why (helps us improve)
                                </label>
                                <textarea
                                    data-testid="self-cancel-reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={3}
                                    maxLength={300}
                                    disabled={submitting}
                                    placeholder="Schedule changed, moving, etc."
                                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red"
                                />
                            </div>

                            <div className="text-[10px] text-zinc-500 leading-relaxed">
                                Full policy:{" "}
                                <a
                                    href="/legal/refund-policy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-zinc-400 hover:text-brand-red inline-flex items-center gap-1"
                                >
                                    Refund &amp; Cancellation Policy <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                            </div>
                        </div>

                        <div className="px-5 py-3 border-t border-zinc-800 flex gap-2 justify-end">
                            <button
                                type="button"
                                data-testid="self-cancel-keep"
                                onClick={() => setOpen(false)}
                                disabled={submitting}
                                className="text-[10px] tracking-[0.25em] uppercase border border-zinc-800 text-zinc-300 hover:text-white hover:border-zinc-600 px-3 py-2 disabled:opacity-50"
                            >
                                Keep membership
                            </button>
                            <button
                                type="button"
                                data-testid="self-cancel-confirm"
                                onClick={submit}
                                disabled={submitting}
                                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase bg-red-700 hover:bg-red-600 text-white px-3 py-2 disabled:opacity-50"
                            >
                                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                                Confirm cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
